/**
 * SchemaImportSource Component
 *
 * Import source for loading fields from existing schemas.
 * Features:
 * - Search input with live filtering
 * - Schema list with field counts
 * - Load button to fetch and parse schema fields
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Layers, ChevronRight } from 'lucide-react';
import { schemaApi, Schema, SchemaField } from '@/services/api/schemaApi';
import { cn } from '@/lib/utils';
import * as errorUtils from '@/utils/errors';

interface SchemaImportSourceProps {
  onFieldsLoaded: (fields: SchemaField[]) => void;
  excludeSchemaId?: string; // Exclude current schema being edited
}

export const SchemaImportSource: React.FC<SchemaImportSourceProps> = ({
  onFieldsLoaded,
  excludeSchemaId,
}) => {
  const [search, setSearch] = useState('');
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);

  // Fetch schemas on mount and search change
  useEffect(() => {
    const fetchSchemas = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await schemaApi.listSchemas({
          search: search.trim() || undefined,
          status: 'published', // Only show published schemas for import
        });

        // Filter out current schema if editing
        const filtered = excludeSchemaId
          ? result.data.filter((s) => s.id !== excludeSchemaId)
          : result.data;

        setSchemas(filtered);
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to load schemas');
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    const debounce = setTimeout(fetchSchemas, 300);
    return () => clearTimeout(debounce);
  }, [search, excludeSchemaId]);

  // Handle schema selection
  const handleSelectSchema = useCallback((schema: Schema) => {
    setSelectedSchema(schema);
  }, []);

  // Load fields from selected schema
  const handleLoadFields = useCallback(() => {
    if (!selectedSchema) return;

    // Generate new IDs for imported fields to avoid conflicts
    const fieldsWithNewIds = selectedSchema.fields.map((field) => ({
      ...field,
      id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    }));

    onFieldsLoaded(fieldsWithNewIds);
  }, [selectedSchema, onFieldsLoaded]);

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search schemas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Schema list */}
      <div className="border rounded-lg max-h-[200px] overflow-y-auto">
        {isLoading && (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Loading schemas...
          </div>
        )}

        {error && !isLoading && (
          <div className="p-4 text-center text-red-500 text-sm">
            {error}
          </div>
        )}

        {!isLoading && !error && schemas.length === 0 && (
          <div className="p-4 text-center text-muted-foreground text-sm">
            {search ? 'No schemas match your search' : 'No published schemas available'}
          </div>
        )}

        {!isLoading && !error && schemas.length > 0 && (
          <div className="divide-y">
            {schemas.map((schema) => (
              <button
                key={schema.id}
                type="button"
                onClick={() => handleSelectSchema(schema)}
                className={cn(
                  'w-full flex items-center gap-3 p-3 text-left hover:bg-accent/50 transition-colors',
                  selectedSchema?.id === schema.id && 'bg-accent'
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {schema.displayName || schema.name}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Layers className="h-3 w-3" />
                      {schema.fields?.length || 0} fields
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {schema.description || 'No description'}
                  </p>
                </div>
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform',
                    selectedSchema?.id === schema.id && 'rotate-90'
                  )}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected schema preview */}
      {selectedSchema && (
        <div className="p-3 rounded-lg bg-muted/50 border">
          <div className="text-sm font-medium mb-1">
            {selectedSchema.displayName || selectedSchema.name}
          </div>
          <div className="text-xs text-muted-foreground">
            Fields: {selectedSchema.fields?.map((f) => f.name).join(', ') || 'None'}
          </div>
        </div>
      )}

      {/* Load button */}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleLoadFields}
          disabled={!selectedSchema}
          className="bg-primary hover:bg-primary/90"
        >
          Load {selectedSchema?.fields?.length || 0} Fields
        </Button>
      </div>
    </div>
  );
};

export default SchemaImportSource;
